import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import './DelioLanding.css';

const ThreeDBackground = () => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const particlesRef = useRef([]);
  const groupRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    const camera = new THREE.PerspectiveCamera(
      75,
      container.clientWidth / container.clientHeight,
      0.1,
      1000
    );
    camera.position.z = 50;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    rendererRef.current = renderer;
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setClearColor(0x000000, 0.1);
    container.appendChild(renderer.domElement);

    // Create group for rotating objects
    const group = new THREE.Group();
    groupRef.current = group;
    scene.add(group);

    // Create floating particles
    const particleGeometry = new THREE.BufferGeometry();
    const particleCount = 100;
    const positions = new Float32Array(particleCount * 3);
    const particles = [];

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 200;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 200;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 200;

      particles.push({
        x: positions[i * 3],
        y: positions[i * 3 + 1],
        z: positions[i * 3 + 2],
        vx: (Math.random() - 0.5) * 0.2,
        vy: (Math.random() - 0.5) * 0.2,
        vz: (Math.random() - 0.5) * 0.2,
      });
    }

    particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const particleMaterial = new THREE.PointsMaterial({
      color: 0xff6b35,
      size: 0.5,
      sizeAttenuation: true,
      opacity: 0.6,
      transparent: true,
    });
    const particleSystem = new THREE.Points(particleGeometry, particleMaterial);
    group.add(particleSystem);
    particlesRef.current = particles;

    // Create wireframe spheres
    for (let i = 0; i < 3; i++) {
      const geometry = new THREE.IcosahedronGeometry(15 + i * 10, 4);
      const material = new THREE.MeshPhongMaterial({
        color: 0xaa1d0d,
        wireframe: true,
        opacity: 0.1 + i * 0.1,
        transparent: true,
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.z = i * 20 - 20;
      group.add(mesh);
    }

    // Create torus knot
    const torusKnotGeometry = new THREE.TorusKnotGeometry(8, 3, 100, 16);
    const torusKnotMaterial = new THREE.MeshPhongMaterial({
      color: 0xff6b35,
      emissive: 0xff6b35,
      emissiveIntensity: 0.3,
      wireframe: false,
      opacity: 0.8,
      transparent: true,
    });
    const torusKnot = new THREE.Mesh(torusKnotGeometry, torusKnotMaterial);
    torusKnot.position.z = -30;
    group.add(torusKnot);

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);

    const pointLight1 = new THREE.PointLight(0xff6b35, 1);
    pointLight1.position.set(50, 50, 50);
    scene.add(pointLight1);

    const pointLight2 = new THREE.PointLight(0xaa1d0d, 0.8);
    pointLight2.position.set(-50, -50, 50);
    scene.add(pointLight2);

    // Mouse tracking
    const mouse = { x: 0, y: 0 };
    const handleMouseMove = (e) => {
      mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
    };
    window.addEventListener('mousemove', handleMouseMove);

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);

      // Update particles
      const positions = particleSystem.geometry.attributes.position.array;
      particles.forEach((particle, i) => {
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.z += particle.vz;

        // Bounce off boundaries
        if (Math.abs(particle.x) > 100) particle.vx *= -1;
        if (Math.abs(particle.y) > 100) particle.vy *= -1;
        if (Math.abs(particle.z) > 100) particle.vz *= -1;

        positions[i * 3] = particle.x;
        positions[i * 3 + 1] = particle.y;
        positions[i * 3 + 2] = particle.z;
      });
      particleSystem.geometry.attributes.position.needsUpdate = true;

      // Rotate group
      group.rotation.x += 0.0002;
      group.rotation.y += 0.0003;

      // Mouse interaction
      group.rotation.x += (mouse.y - group.rotation.x) * 0.05;
      group.rotation.y += (mouse.x - group.rotation.y) * 0.05;

      renderer.render(scene, camera);
    };
    animate();

    // Handle window resize
    const handleResize = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      if (container && renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        opacity: 0.5,
      }}
    />
  );
};

const NiblLanding = () => {
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      icon: "📱",
      title: "Seamless Mobile Experience",
      description: "Beautiful, intuitive app for both iOS and Android. Track deals and rewards on the go."
    },
    {
      icon: "🎁",
      title: "Exclusive Deals & Rewards",
      description: "Get access to fire deals and earn loyalty points with every purchase."
    },
    {
      icon: "👥",
      title: "For Merchants & Customers",
      description: "Merchants manage deals easily while customers discover amazing savings nearby."
    },
    {
      icon: "📈",
      title: "Real-time Analytics",
      description: "Track performance and customer engagement with comprehensive dashboards."
    }
  ];

  const benefits = [
    "Find deals near you instantly",
    "Earn loyalty points on purchases",
    "Get exclusive merchant offers",
    "Share deals with friends",
    "Manage your business promotions",
    "Grow your customer base"
  ];

  return (
    <div className="Nibl-landing">
      <ThreeDBackground />
      {/* Hero Section */}
      <section className="hero" style={{ position: 'relative', zIndex: 1 }}>
        <div className="hero-content">
          <h1 className="hero-title">
            Discover <span className="highlight">Local Deals</span> & Earn <span className="highlight">Rewards</span>
          </h1>
          <p className="hero-subtitle">
            Nibl connects you with amazing deals from nearby businesses while earning loyalty rewards
          </p>
          
          <div className="hero-cta">
            <button className="btn btn-primary">
              Download App →
            </button>
            <button className="btn btn-secondary">
              Learn More
            </button>
          </div>
        </div>

        <div className="hero-visual">
          <div className="phone-mockup">
            <div className="phone-screen">
              <div className="screen-content">
                <div className="app-bar">Nibl</div>
                <div className="deal-card">
                  <div className="deal-image" style={{
                    backgroundImage: 'linear-gradient(135deg, #aa1d0d, #ff6b35)',
                    width: '100%',
                    borderRadius: '8px 8px 0 0'
                  }}></div>
                  <div className="deal-content">
                    <div className="deal-badge">Fire Deal</div>
                    <div className="deal-info">
                      <div className="deal-title">☕ Coffee Shop</div>
                      <div className="deal-price">50% Off</div>
                    </div>
                    <div className="deal-meta">
                      <span className="deal-status">Active</span>
                      <span className="deal-likes">❤️ 34</span>
                    </div>
                  </div>
                </div>
                <div className="deal-card">
                  <div className="deal-image" style={{
                    backgroundImage: 'linear-gradient(135deg, #aa1d0d, #ff6b35)',
                    width: '100%',
                    borderRadius: '8px 8px 0 0'
                  }}></div>
                  <div className="deal-content">
                    <div className="deal-badge">Fire Deal</div>
                    <div className="deal-info">
                      <div className="deal-title">☕ Chicken Tikka Masala</div>
                      <div className="deal-price">15% Off</div>
                    </div>
                    <div className="deal-meta">
                      <span className="deal-status">Active</span>
                      <span className="deal-likes">❤️ 24</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Merchant Tools Section */}
      <section className="merchant-tools" style={{ position: 'relative', zIndex: 1 }}>
        <div className="section-header">
          <h2>Complete Merchant Management Tools</h2>
          <p>A fully functional app designed to bridge the gap between your business and customers</p>
        </div>

        <div className="tools-grid">
          <div className="tool-card">
            <div className="tool-icon">📋</div>
            <h3>Manage Menu</h3>
            <p>Update your menu items, descriptions, prices, and photos in real-time. Keep everything current and accurate.</p>
            <div className="tool-example">Example: Add your new signature dish with a beautiful photo</div>
          </div>

          <div className="tool-card">
            <div className="tool-icon">⏰</div>
            <h3>Hours of Operation</h3>
            <p>Set your business hours and communicate schedule changes instantly to all your customers.</p>
            <div className="tool-example">Example: "We're closed on Sundays" - reaches customers instantly</div>
          </div>

          <div className="tool-card">
            <div className="tool-icon">📢</div>
            <h3>Make Announcements</h3>
            <p>Send important updates directly to your customers. Notify them about special events, promotions, or changes.</p>
            <div className="tool-example">Example: "New summer menu available this week!"</div>
          </div>

          <div className="tool-card">
            <div className="tool-icon">🔥</div>
            <h3>Create Fire Deals</h3>
            <p>Launch limited-time offers to attract customers and boost sales. Use deals to promote new items and move inventory.</p>
            <div className="tool-example">Example: "50% off our new signature burger - this weekend only!"</div>
          </div>

          <div className="tool-card">
            <div className="tool-icon">📊</div>
            <h3>Track Analytics</h3>
            <p>Monitor deal performance, customer engagement, and revenue in real-time. Make data-driven decisions.</p>
            <div className="tool-example">Example: See which deals are most popular and adjust your strategy</div>
          </div>

          <div className="tool-card">
            <div className="tool-icon">👥</div>
            <h3>Customer Insights</h3>
            <p>Understand your customer base better. View preferences, purchase history, and engagement metrics.</p>
            <div className="tool-example">Example: Identify top customers and reward them with exclusive offers</div>
          </div>
        </div>

        <div className="tools-summary">
          <p>Nibl gives merchants everything needed to run their business efficiently and connect meaningfully with customers - all in one beautifully designed app.</p>
        </div>
      </section>
      {/* Features Section */}
      <section className="features" style={{ position: 'relative', zIndex: 1 }}>
        <div className="section-header">
          <h2>Why Choose Nibl?</h2>
          <p>Everything you need to find deals and manage rewards</p>
        </div>

        <div className="features-grid">
          {features.map((feature, index) => (
            <div 
              key={index}
              className={`feature-card ${activeFeature === index ? 'active' : ''}`}
              onMouseEnter={() => setActiveFeature(index)}
            >
              <div className="feature-icon-wrapper">
                <span className="feature-emoji">{feature.icon}</span>
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* For Customers Section */}
      <section className="for-customers" style={{ position: 'relative', zIndex: 1 }}>
        <div className="container-split">
          <div className="split-content">
            <h2>For Customers</h2>
            <p className="intro-text">
              Discover local businesses offering exclusive deals and build your loyalty rewards.
            </p>
            
            <div className="benefits-list">
              {benefits.slice(0, 3).map((benefit, index) => (
                <div key={index} className="benefit-item">
                  <span className="check-icon">✓</span>
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            <button className="btn btn-primary">
              Get Started →
            </button>
          </div>
          
          <div className="split-visual">
            <div className="feature-highlight">
              <div className="highlight-card">
                <div className="card-header">Nearby Deals</div>
                <div className="card-body">
                  <div className="deal-item">🍕 Pizza Place - 30% Off</div>
                  <div className="deal-item">☕ Coffee Shop - Buy 1 Get 1</div>
                  <div className="deal-item">🍔 Burger King - $5 Off</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Merchants Section */}
      <section className="for-merchants" style={{ position: 'relative', zIndex: 1 }}>
        <div className="container-split reverse">
          <div className="split-visual">
            <div className="feature-highlight merchant">
              <div className="highlight-card">
                <div className="card-header">Manage Deals</div>
                <div className="card-body">
                  <div className="metric">Total Deals: 12</div>
                  <div className="metric">Active Users: 234</div>
                  <div className="metric">Revenue: $5,432</div>
                </div>
              </div>
            </div>
          </div>

          <div className="split-content">
            <h2>For Merchants</h2>
            <p className="intro-text">
              Grow your business by creating and managing deals that reach nearby customers instantly.
            </p>
            
            <div className="benefits-list">
              {benefits.slice(3, 6).map((benefit, index) => (
                <div key={index} className="benefit-item">
                  <span className="check-icon">✓</span>
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            <button className="btn btn-primary">
              Sign Up as Merchant →
            </button>
          </div>
        </div>
      </section>


      {/* How It Works */}
      <section className="how-it-works" style={{ position: 'relative', zIndex: 1 }}>
        <div className="section-header">
          <h2>How It Works</h2>
          <p>Simple steps to get started</p>
        </div>

        <div className="steps-grid">
          {[
            { num: 1, title: "Download", desc: "Get the Nibl app on your device" },
            { num: 2, title: "Sign Up", desc: "Create your account in seconds" },
            { num: 3, title: "Explore", desc: "Find deals near you" },
            { num: 4, title: "Earn", desc: "Collect rewards and save money" }
          ].map((step, index) => (
            <div key={index} className="step-card">
              <div className="step-number">{step.num}</div>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
              {index < 3 && <div className="step-arrow">→</div>}
            </div>
          ))}
        </div>
      </section>

      {/* Ready to Save and Earn Section */}
      <section className="ready-section" style={{ position: 'relative', zIndex: 1 }}>
        <div className="ready-content">
          <h2>Ready to Save & Earn?</h2>
          <p>Start your journey to better deals and exclusive rewards today</p>
          
          <div className="ready-features">
            <div className="ready-item">
              <div className="ready-icon">💰</div>
              <h3>Save Money</h3>
              <p>Access exclusive deals from your favorite local businesses</p>
            </div>
            <div className="ready-item">
              <div className="ready-icon">⭐</div>
              <h3>Earn Rewards</h3>
              <p>Collect loyalty points on every purchase</p>
            </div>
            <div className="ready-item">
              <div className="ready-icon">🚀</div>
              <h3>Grow Your Business</h3>
              <p>For merchants: reach more customers and increase revenue</p>
            </div>
          </div>

          <div className="ready-cta">
            <button className="btn btn-primary btn-lg">
              Download Now →
            </button>
            <button className="btn btn-secondary btn-lg">
              Learn More
            </button>
          </div>
        </div>
      </section>


 
      {/* Footer CTA */}
      <section className="footer-cta" style={{ position: 'relative', zIndex: 1 }}>
        <div className="footer-content">
          <div className="footer-section">
            <h4>Product</h4>
            <ul>
              <li><a href="#features">Features</a></li>
              <li><a href="#pricing">Pricing</a></li>
              <li><a href="#download">Download</a></li>
            </ul>
          </div>

          <div className="footer-section">
            <h4>Company</h4>
            <ul>
              <li><a href="#about">About Us</a></li>
              <li><a href="#careers">Careers</a></li>
              <li><a href="#blog">Blog</a></li>
            </ul>
          </div>

          <div className="footer-section">
            <h4>Legal</h4>
            <ul>
              <li><a href="#privacy">Privacy</a></li>
              <li><a href="#terms">Terms</a></li>
              <li><a href="#contact">Contact</a></li>
            </ul>
          </div>

          <div className="footer-section social">
            <h4>Follow Us</h4>
            <div className="social-links">
              <a href="#twitter">Twitter</a>
              <a href="#instagram">Instagram</a>
              <a href="#facebook">Facebook</a>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; 2025 Nibl. All rights reserved.</p>
        </div>
      </section>
    </div>
  );
};

export default NiblLanding;
